import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Trash2 } from '@/components/icons';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { formatINR, formatDate } from '@/lib/format';
import { listSisSalesBatches, deleteSisSalesBatch, type SisSalesBatch } from '@/api/sisStock';

interface Props {
  open: boolean;
  onClose: () => void;
  filter: { group?: string; partyId?: number };
}

/** Recent sales-report uploads — review and delete (fully reverse) a wrong one. */
export function SalesUploadsModal({ open, onClose, filter }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const [pending, setPending] = useState<SisSalesBatch | null>(null);

  const q = useQuery({
    queryKey: ['sis-sales-batches', filter.group ?? '', filter.partyId ?? ''],
    queryFn: () => listSisSalesBatches(filter),
    enabled: open,
  });
  const batches = q.data ?? [];

  const delMut = useMutation({
    mutationFn: (batchNo: string) => deleteSisSalesBatch(batchNo),
    onSuccess: (r) => {
      toast.push(`Upload ${r.batchNo} deleted — ${r.deletedLines} line(s) reversed`, 'success');
      setPending(null);
      q.refetch();
      qc.invalidateQueries({ queryKey: ['sis-sales'] });
      qc.invalidateQueries({ queryKey: ['sis-stock'] });
      qc.invalidateQueries({ queryKey: ['sis-dashboard'] });
    },
    onError: (e) => { setPending(null); toast.push(apiError(e, 'Delete failed'), 'error'); },
  });

  const dateRange = (b: SisSalesBatch) =>
    b.firstDate === b.lastDate ? formatDate(b.firstDate) : `${formatDate(b.firstDate)} – ${formatDate(b.lastDate)}`;

  return (
    <>
      <Modal open={open} onClose={onClose} title="Recent sales uploads" size="lg">
        <div className="flex flex-col gap-3">
          <p className="text-xs text-[var(--text-muted)]">
            Delete a wrongly-uploaded report — it fully reverses that batch (units back on hand, FIFO lots restored, ledger &amp; sale lines removed).
            {filter.group || filter.partyId ? ' Filtered to the current store/group.' : ' Showing all recent uploads.'}
          </p>
          <div className="overflow-x-auto rounded-md border border-[var(--border-soft)]">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-orange-50 text-left text-[10px] font-black uppercase tracking-wide text-accent">
                  <th className="border-b border-[var(--border-color)] px-2 py-1.5">Batch</th>
                  <th className="border-b border-[var(--border-color)] px-2 py-1.5">Store / Group</th>
                  <th className="border-b border-[var(--border-color)] px-2 py-1.5">Dates</th>
                  <th className="border-b border-[var(--border-color)] px-2 py-1.5 text-right">Lines</th>
                  <th className="border-b border-[var(--border-color)] px-2 py-1.5 text-right">Units</th>
                  <th className="border-b border-[var(--border-color)] px-2 py-1.5 text-right">Net Value</th>
                  <th className="border-b border-[var(--border-color)] px-2 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {q.isLoading && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-[var(--text-muted)]">Loading…</td></tr>
                )}
                {!q.isLoading && batches.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-[var(--text-muted)]">No sales uploads found.</td></tr>
                )}
                {batches.map((b, i) => (
                  <tr key={b.batchNo} className={i % 2 ? 'bg-[var(--bg-secondary)]/60' : ''}>
                    <td className="border-r border-[var(--border-color)] px-2 py-1.5 font-mono">
                      {b.batchNo}{b.isLatest && <span className="ml-1"><Badge tone="success">latest</Badge></span>}
                    </td>
                    <td className="border-r border-[var(--border-color)] px-2 py-1.5">{b.storeLabel}</td>
                    <td className="border-r border-[var(--border-color)] px-2 py-1.5 whitespace-nowrap">{dateRange(b)}</td>
                    <td className="border-r border-[var(--border-color)] px-2 py-1.5 text-right">{b.lines}</td>
                    <td className="border-r border-[var(--border-color)] px-2 py-1.5 text-right">{b.units}</td>
                    <td className="border-r border-[var(--border-color)] px-2 py-1.5 text-right">₹{formatINR(b.netValue)}</td>
                    <td className="px-2 py-1.5 text-right">
                      <Button variant="danger" size="sm" onClick={() => setPending(b)} disabled={delMut.isPending}>
                        <span className="inline-flex items-center gap-1"><Trash2 size={12} /> Delete</span>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        open={!!pending}
        onClose={() => { if (!delMut.isPending) setPending(null); }}
        title="Delete this sales upload?"
        size="sm"
        footer={
          <>
            <Button variant="glass" onClick={() => setPending(null)} disabled={delMut.isPending}>Cancel</Button>
            <Button variant="danger" onClick={() => pending && delMut.mutate(pending.batchNo)} disabled={delMut.isPending}>
              {delMut.isPending ? 'Deleting…' : 'Delete & reverse'}
            </Button>
          </>
        }
      >
        {pending && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-[var(--text-secondary)]">
              Batch <span className="font-mono font-bold text-[var(--text-primary)]">{pending.batchNo}</span> ({pending.storeLabel}) —
              {' '}{pending.lines} line{pending.lines === 1 ? '' : 's'}, {pending.units} units.
            </p>
            <div className="rounded-md bg-danger/10 px-3 py-2 text-xs text-danger">
              This reverses the whole upload: sold units go back on hand, the FIFO lots it consumed are restored, and its sale lines, allocations, returns and ledger entries are removed. This can&apos;t be undone.
            </div>
            {!pending.isLatest && (
              <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/[0.06] px-3 py-2 text-xs text-warning">
                <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
                <span>This is <strong>not the latest</strong> upload. A newer upload may have consumed the stock this one added, so lot costs afterwards can become approximate. Prefer deleting newest-first.</span>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
